import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Popover,
  Select,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import moment from "moment";
import AgGridTable from "../../components/table/AgGridTable";
import axiosInstance from "../../axios/axios";
import { END_POINTS } from "../../shared/endpoints";
import AwsLogo from "../../assets/baseVersion/aws.svg";
import AwsLogoDark from "../../assets/baseVersion/AWS_DARK.svg";

type GroupBy = "none" | "service" | "region";
type ViewMode = "list" | "grid";

interface InventoryDevice {
  id: string;
  resource_name: string;
  arn: string;
  vendor: string;
  account_id: string;
  region: string;
  service: string;
  resource_type: string;
  scan_type?: string;
  last_seen: string;
  comments: string;
}

interface InventoryGroup {
  key: string;
  count: number;
}

type TableRow = InventoryDevice & {
  isGroup?: boolean;
  isChild?: boolean;
  isExpanded?: boolean;
  group_key?: string;
  parent_group_key?: string;
  entriesCount?: number;
  loadingChildren?: boolean;
};

const PAGE_SIZE_OPTIONS = [50, 100] as const;
const DEFAULT_PAGE_SIZE = 50;

const formatWhen = (value: string) => {
  if (!value) return "-";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("DD-MMM-YYYY, HH:mm:ss") : value;
};

const normKey = (value?: string) => String(value || "").trim().toLowerCase();

const isAwsVendor = (vendor?: string) => {
  const value = normKey(vendor);
  return value === "aws" || value === "amazon web services" || value.includes("amazon") || value.includes("aws");
};

const VendorCell = ({
  vendor,
  isDarkMode,
  isGroup,
  entriesCount,
}: {
  vendor?: string;
  isDarkMode: boolean;
  isGroup?: boolean;
  entriesCount?: number;
}) => {
  if (isGroup) {
    return (
      <Typography variant="body2" sx={{ display: "flex", alignItems: "center", height: "100%" }}>
        {entriesCount ?? 0} resources
      </Typography>
    );
  }
  if (isAwsVendor(vendor)) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", height: "100%", width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 56,
            height: 32,
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: isDarkMode ? "rgba(255,255,255,0.04)" : "#FFFFFF",
            px: 0.75,
          }}
        >
          <img
            src={isDarkMode ? AwsLogoDark : AwsLogo}
            alt="AWS"
            style={{ width: 40, height: 22, objectFit: "contain" }}
          />
        </Box>
      </Box>
    );
  }
  return (
    <Typography variant="body2" noWrap title={vendor || "-"}>
      {vendor || "-"}
    </Typography>
  );
};

const Detail = ({ label, value }: { label: string; value?: string }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ wordBreak: "break-all" }}>{value || "-"}</Typography>
  </Box>
);

const AwsInventory = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const locationScanId = (location.state as { scanId?: string } | null)?.scanId || "";
  const scanId = (searchParams.get("scan_id") || locationScanId || "").trim();
  const [groupBy, setGroupBy] = useState<GroupBy>("none");
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({ total_resources: 0, services: 0, regions: 0 });
  const [devices, setDevices] = useState<InventoryDevice[]>([]);
  const [groups, setGroups] = useState<InventoryGroup[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selected, setSelected] = useState<InventoryDevice | null>(null);
  const [filtersAnchor, setFiltersAnchor] = useState<HTMLElement | null>(null);
  const [vendorFilter, setVendorFilter] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [groupChildren, setGroupChildren] = useState<Record<string, InventoryDevice[]>>({});
  const [loadingGroups, setLoadingGroups] = useState<Record<string, boolean>>({});
  const groupChildrenRef = useRef(groupChildren);
  const loadingGroupsRef = useRef(loadingGroups);
  const groupByRef = useRef(groupBy);
  groupChildrenRef.current = groupChildren;
  loadingGroupsRef.current = loadingGroups;
  groupByRef.current = groupBy;

  const showingGroups = groupBy !== "none";

  useEffect(() => {
    setPage(1);
    setExpandedGroups({});
    setGroupChildren({});
    setLoadingGroups({});
  }, [scanId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(END_POINTS.AWS_INVENTORY, {
        params: {
          page,
          page_size: pageSize,
          search,
          group_by: groupBy,
          ...(scanId ? { scan_id: scanId } : {}),
        },
      });
      const data = response.data?.data || response.data || {};
      const pageInfo = data.pagination || response.data?.pagination || {};
      const nextTotal = Number(pageInfo.total || 0);
      const nextTotalPages = Number(
        pageInfo.total_pages || (nextTotal > 0 ? Math.ceil(nextTotal / pageSize) : 0),
      );
      setSummary(data.summary || { total_resources: 0, services: 0, regions: 0 });
      setDevices(data.devices || []);
      setGroups(data.groups || []);
      setTotal(nextTotal);
      setTotalPages(nextTotalPages);
    } catch {
      setDevices([]);
      setGroups([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [groupBy, page, pageSize, scanId, search]);

  useEffect(() => {
    const handle = window.setTimeout(load, 250);
    return () => window.clearTimeout(handle);
  }, [load]);

  const fetchGroupDevices = useCallback(async (groupKey: string, mode: GroupBy) => {
    if (mode === "none" || !groupKey) return;
    setLoadingGroups((prev) => ({ ...prev, [groupKey]: true }));
    try {
      const collected: InventoryDevice[] = [];
      let nextPage = 1;
      let hasNext = true;
      const wanted = normKey(groupKey);
      while (hasNext) {
        const response = await axiosInstance.get(END_POINTS.AWS_INVENTORY, {
          params: {
            page: nextPage,
            page_size: pageSize,
            search,
            group_by: "none",
            ...(scanId ? { scan_id: scanId } : {}),
            ...(mode === "service" ? { service: groupKey } : { region: groupKey }),
          },
        });
        const data = response.data?.data || {};
        const pageDevices = (data.devices || []) as InventoryDevice[];
        const scoped = pageDevices.filter((row) =>
          mode === "service"
            ? normKey(row.service) === wanted
            : normKey(row.region) === wanted,
        );
        collected.push(...scoped);
        hasNext = Boolean(data.pagination?.has_next);
        nextPage += 1;
        if (nextPage > 50) break;
      }
      setGroupChildren((prev) => ({ ...prev, [groupKey]: collected }));
    } catch {
      setGroupChildren((prev) => ({ ...prev, [groupKey]: [] }));
    } finally {
      setLoadingGroups((prev) => ({ ...prev, [groupKey]: false }));
    }
  }, [pageSize, scanId, search]);

  const toggleGroup = useCallback((groupKey: string) => {
    if (!groupKey) return;
    setExpandedGroups((prev) => {
      const willExpand = !prev[groupKey];
      if (
        willExpand &&
        !groupChildrenRef.current[groupKey] &&
        !loadingGroupsRef.current[groupKey]
      ) {
        void fetchGroupDevices(groupKey, groupByRef.current);
      }
      return { ...prev, [groupKey]: willExpand };
    });
  }, [fetchGroupDevices]);

  const goToPage = useCallback((nextPage: number) => {
    if (loading) return;
    if (nextPage < 1) return;
    if (totalPages > 0 && nextPage > totalPages) return;
    if (nextPage === page) return;
    setPage(nextPage);
  }, [loading, page, totalPages]);

  const changePageSize = (nextSize: number) => {
    if (!PAGE_SIZE_OPTIONS.includes(nextSize as (typeof PAGE_SIZE_OPTIONS)[number])) return;
    if (nextSize === pageSize) return;
    setPageSize(nextSize);
    setPage(1);
    setExpandedGroups({});
    setGroupChildren({});
    setLoadingGroups({});
  };

  const changeGroupBy = (next: GroupBy) => {
    setGroupBy(next);
    setPage(1);
    setVendorFilter([]);
    setTypeFilter([]);
    setExpandedGroups({});
    setGroupChildren({});
    setLoadingGroups({});
  };

  const selectSummaryCard = (cardId: GroupBy) => {
    if (cardId === "none") {
      changeGroupBy("none");
      return;
    }
    // Toggle Services / Regions: click again returns to the main resource list
    changeGroupBy(groupBy === cardId ? "none" : cardId);
  };

  const vendors = useMemo(
    () => [...new Set(devices.map((row) => row.vendor).filter(Boolean))].sort(),
    [devices],
  );
  const deviceTypes = useMemo(
    () => [...new Set(devices.map((row) => row.resource_type).filter(Boolean))].sort(),
    [devices],
  );

  const matchesFilter = useCallback((row: InventoryDevice) => {
    const vendorOk = vendorFilter.length === 0 || vendorFilter.includes(row.vendor);
    const typeOk = typeFilter.length === 0 || typeFilter.includes(row.resource_type);
    return vendorOk && typeOk;
  }, [vendorFilter, typeFilter]);

  const tableRows: TableRow[] = useMemo(() => {
    if (!showingGroups) {
      return devices.filter(matchesFilter).map((row) => ({ ...row }));
    }

    const rows: TableRow[] = [];
    groups.forEach((group) => {
      const isExpanded = Boolean(expandedGroups[group.key]);
      const wanted = normKey(group.key);
      const children = (groupChildren[group.key] || []).filter((row) => {
        if (!matchesFilter(row)) return false;
        if (groupBy === "service") return normKey(row.service) === wanted;
        if (groupBy === "region") return normKey(row.region) === wanted;
        return true;
      });
      rows.push({
        id: `group-${group.key}`,
        resource_name: group.key,
        arn: "",
        vendor: "",
        account_id: "",
        region: groupBy === "region" ? group.key : "",
        service: groupBy === "service" ? group.key : "",
        resource_type: "",
        last_seen: "",
        comments: "",
        isGroup: true,
        isExpanded,
        group_key: group.key,
        entriesCount: group.count,
        loadingChildren: Boolean(loadingGroups[group.key]),
      });
      if (isExpanded) {
        if (loadingGroups[group.key] && children.length === 0) {
          rows.push({
            id: `loading-${group.key}`,
            resource_name: "Loading resources...",
            arn: "",
            vendor: "",
            account_id: "",
            region: "",
            service: "",
            resource_type: "",
            last_seen: "",
            comments: "",
            isChild: true,
            parent_group_key: group.key,
            loadingChildren: true,
          });
        } else {
          children.forEach((child) => {
            rows.push({
              ...child,
              isChild: true,
              parent_group_key: group.key,
            });
          });
        }
      }
    });
    return rows;
  }, [
    devices,
    expandedGroups,
    groupBy,
    groupChildren,
    groups,
    loadingGroups,
    matchesFilter,
    showingGroups,
  ]);

  const startRow = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endRow = Math.min(page * pageSize, total);
  const hasPreviousPage = page > 1;
  const hasNextPage = page < totalPages;

  const openDevice = useCallback((row: TableRow) => {
    if (!row || row.isGroup || row.loadingChildren) return;
    setSelected(row);
  }, []);

  const cards: Array<{ id: GroupBy; label: string; value: number; icon: typeof CloudOutlinedIcon; color: string }> = [
    { id: "none", label: "Total resources", value: summary.total_resources, icon: CloudOutlinedIcon, color: "#3B82F6" },
    { id: "service", label: "Services", value: summary.services, icon: CategoryOutlinedIcon, color: "#6366F1" },
    { id: "region", label: "Regions", value: summary.regions, icon: PublicOutlinedIcon, color: "#8B5CF6" },
  ];

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Hostname",
        field: "resource_name",
        minWidth: 260,
        sortable: true,
        filter: true,
        cellRenderer: (params: { data?: TableRow; value?: string }) => {
          const row = params.data;
          if (!row) return null;
          if (row.isGroup) {
            return (
              <Box sx={{ display: "flex", alignItems: "center", height: "100%", width: "100%", cursor: "pointer" }}>
                <KeyboardArrowDownIcon
                  sx={{
                    transform: row.isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                    transition: "transform 0.2s",
                    mr: 1,
                    fontSize: 18,
                    color: isDarkMode ? "#E5E7EB" : "#344054",
                  }}
                />
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "13px", mr: 1.5, color: isDarkMode ? "#FFFFFF" : "#101828" }}>
                  {params.value}
                </Typography>
                <Box
                  sx={{
                    minWidth: 20,
                    height: 20,
                    px: 0.75,
                    borderRadius: "999px",
                    backgroundColor: isDarkMode ? "#3B82F6" : "#4A7BF7",
                    color: "#FFFFFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "11px",
                    fontWeight: 700,
                    lineHeight: 1,
                    flexShrink: 0,
                  }}
                >
                  {row.entriesCount ?? 0}
                </Box>
                {row.loadingChildren ? <CircularProgress size={14} sx={{ ml: 1 }} /> : null}
              </Box>
            );
          }
          return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%", pl: row.isChild ? 4 : 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: row.isChild ? 500 : 700,
                  fontSize: "12px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%",
                }}
              >
                {row.loadingChildren ? "Loading resources..." : (params.value || "-")}
              </Typography>
            </Box>
          );
        },
      },
      {
        headerName: "Vendor",
        field: "vendor",
        minWidth: 140,
        width: 160,
        sortable: true,
        filter: true,
        cellRenderer: (params: { data?: TableRow; value?: string }) => (
          <VendorCell
            vendor={params.value}
            isDarkMode={isDarkMode}
            isGroup={params.data?.isGroup}
            entriesCount={params.data?.entriesCount}
          />
        ),
      },
      {
        headerName: "Account ID",
        field: "account_id",
        minWidth: 160,
        sortable: true,
        filter: true,
        valueFormatter: (params: { data?: TableRow; value?: string }) =>
          params.data?.isGroup || params.data?.loadingChildren ? "-" : (params.value || "-"),
      },
      {
        headerName: "Device type",
        field: "resource_type",
        minWidth: 150,
        sortable: true,
        filter: true,
        valueFormatter: (params: { data?: TableRow; value?: string }) =>
          params.data?.isGroup || params.data?.loadingChildren ? "-" : (params.value || "-"),
      },
      {
        headerName: "Timestamp",
        field: "last_seen",
        minWidth: 200,
        sortable: true,
        valueFormatter: (params: { data?: TableRow; value?: string }) =>
          params.data?.isGroup || params.data?.loadingChildren ? "-" : formatWhen(params.value || ""),
      },
      {
        headerName: "Comments",
        field: "comments",
        minWidth: 120,
        valueFormatter: (params: { data?: TableRow; value?: string }) =>
          params.data?.isGroup || params.data?.loadingChildren ? "-" : (params.value || "-"),
      },
      {
        headerName: "",
        field: "actions",
        width: 70,
        maxWidth: 80,
        sortable: false,
        filter: false,
        cellRenderer: (params: { data?: TableRow }) => {
          if (!params.data || params.data.isGroup || params.data.loadingChildren) return null;
          return (
            <IconButton
              size="small"
              aria-label="Resource actions"
              onClick={(event) => {
                event.stopPropagation();
                openDevice(params.data as TableRow);
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          );
        },
      },
    ],
    [isDarkMode, openDevice],
  );

  const gridOptions = useMemo(
    () => ({
      rowHeight: 50,
      domLayout: "normal" as const,
      animateRows: true,
      getRowId: (params: { data?: TableRow }) =>
        params?.data?.id || params?.data?.arn || params?.data?.group_key || JSON.stringify(params?.data),
      postSortRows: (params: { nodes: Array<{ data?: TableRow }> }) => {
        if (!showingGroups) return;
        const rowNodes = params.nodes;
        const sortedGroups: typeof rowNodes = [];
        const childMap = new Map<string, typeof rowNodes>();
        rowNodes.forEach((node) => {
          if (node.data?.isGroup) {
            sortedGroups.push(node);
          } else if (node.data?.isChild) {
            const parent = node.data.parent_group_key || "";
            if (!childMap.has(parent)) childMap.set(parent, []);
            childMap.get(parent)!.push(node);
          }
        });
        if (sortedGroups.length === 0) return;
        let nextInsertPos = 0;
        sortedGroups.forEach((groupNode) => {
          rowNodes[nextInsertPos++] = groupNode;
          const children = childMap.get(groupNode.data?.group_key || "");
          children?.forEach((childNode) => {
            rowNodes[nextInsertPos++] = childNode;
          });
        });
      },
      rowClassRules: {
        "ag-row-group": (params: { data?: TableRow }) => params.data?.isGroup === true,
        "ag-row-child": (params: { data?: TableRow }) => params.data?.isChild === true,
      },
      onCellClicked: (params: { data?: TableRow }) => {
        const row = params.data;
        if (!row) return;
        if (row.isGroup && row.group_key) {
          toggleGroup(row.group_key);
          return;
        }
        openDevice(row);
      },
    }),
    [openDevice, showingGroups, toggleGroup],
  );

  const toggleFacet = (current: string[], value: string, set: (next: string[]) => void) => {
    set(current.includes(value) ? current.filter((item) => item !== value) : [...current, value]);
  };

  const pageNumbers = useMemo(() => {
    if (totalPages <= 0) return [];
    const windowStart = Math.max(1, page - 2);
    const windowEnd = Math.min(totalPages, windowStart + 4);
    return Array.from({ length: Math.max(0, windowEnd - windowStart + 1) }, (_, index) => windowStart + index);
  }, [page, totalPages]);

  const filtersOn = vendorFilter.length > 0 || typeFilter.length > 0;
  const headerActions = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {!showingGroups ? (
        <Button
          size="small"
          variant={filtersOn || Boolean(filtersAnchor) ? "contained" : "outlined"}
          startIcon={<FilterListIcon />}
          endIcon={filtersAnchor ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          onClick={(event) => setFiltersAnchor(event.currentTarget)}
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5 }}
        >
          Filters
        </Button>
      ) : null}
      <Box sx={{ display: "flex", border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 0.25, gap: 0.25 }}>
        <IconButton
          size="small"
          onClick={() => setViewMode("list")}
          title="Table View"
          sx={{
            borderRadius: 1,
            color: viewMode === "list" ? "primary.main" : "text.secondary",
            bgcolor: viewMode === "list" ? "action.selected" : "transparent",
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          <ViewListIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => setViewMode("grid")}
          title="Grid View"
          sx={{
            borderRadius: 1,
            color: viewMode === "grid" ? "primary.main" : "text.secondary",
            bgcolor: viewMode === "grid" ? "action.selected" : "transparent",
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          <ViewModuleIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "calc(100vh - 120px)",
        maxHeight: "calc(100vh - 120px)",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      {scanId ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, flexShrink: 0 }}>
          Showing resources discovered in this scan
        </Typography>
      ) : null}
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", flexShrink: 0 }}>
        {cards.map((card) => {
          const isSelected = groupBy === card.id;
          return (
            <Box
              key={card.id}
              onClick={() => selectSummaryCard(card.id)}
              sx={{
                flex: "1 1 190px",
                minWidth: 180,
                px: 2.75,
                py: 2.25,
                borderRadius: 2.5,
                border: "2px solid",
                borderColor: isSelected ? card.color : "divider",
                bgcolor: isSelected
                  ? isDarkMode
                    ? `${card.color}22`
                    : `${card.color}0D`
                  : "background.paper",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                gap: 1,
                boxShadow: isSelected ? `0 4px 14px ${card.color}33` : "0 1px 3px rgba(0,0,0,0.04)",
                transition: "all 0.15s ease-in-out",
                userSelect: "none",
                "&:hover": { boxShadow: `0 4px 14px ${card.color}25`, borderColor: card.color, transform: "translateY(-1px)" },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <card.icon sx={{ fontSize: 26, color: card.color, flexShrink: 0 }} />
                <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.5rem", lineHeight: 1 }}>
                  {card.value}
                </Typography>
              </Box>
              <Typography
                variant="body2"
                fontWeight={isSelected ? 700 : 500}
                color={isSelected ? "text.primary" : "text.secondary"}
                sx={{ fontSize: "0.925rem", lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {card.label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Card
        variant="outlined"
        sx={{
          flex: 1,
          minHeight: 0,
          borderRadius: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Popover
          open={Boolean(filtersAnchor)}
          anchorEl={filtersAnchor}
          onClose={() => setFiltersAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Box sx={{ p: 2, width: 280, maxHeight: 360, overflow: "auto" }}>
            <Typography variant="subtitle2" fontWeight={700}>Vendor</Typography>
            {vendors.map((vendor) => (
              <Box key={vendor} sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox size="small" checked={vendorFilter.includes(vendor)} onChange={() => toggleFacet(vendorFilter, vendor, setVendorFilter)} />
                <Typography variant="body2">{vendor}</Typography>
              </Box>
            ))}
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" fontWeight={700}>Device type</Typography>
            {deviceTypes.map((type) => (
              <Box key={type} sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox size="small" checked={typeFilter.includes(type)} onChange={() => toggleFacet(typeFilter, type, setTypeFilter)} />
                <Typography variant="body2">{type}</Typography>
              </Box>
            ))}
            <Button size="small" sx={{ mt: 1, textTransform: "none" }} onClick={() => { setVendorFilter([]); setTypeFilter([]); }}>
              Clear
            </Button>
          </Box>
        </Popover>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            "& .ag-theme-material": { height: "100% !important" },
            "& .ag-root-wrapper": { height: "100% !important" },
            "& .ag-body-viewport": { overflowY: "auto !important" },
          }}
        >
          {viewMode === "grid" ? (
            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1, flexShrink: 0 }}>{headerActions}</Box>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 2 }}>
                {showingGroups
                  ? groups.map((group) => (
                    <Box
                      key={group.key}
                      onClick={() => toggleGroup(group.key)}
                      sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider", cursor: "pointer" }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <KeyboardArrowDownIcon
                          sx={{
                            transform: expandedGroups[group.key] ? "rotate(0deg)" : "rotate(-90deg)",
                            transition: "transform 0.2s",
                            fontSize: 18,
                          }}
                        />
                        <Typography variant="subtitle2" fontWeight={700} noWrap>{group.key}</Typography>
                        <Box sx={{ ml: "auto", minWidth: 20, height: 20, px: 0.75, borderRadius: "999px", bgcolor: "primary.main", color: "#fff", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {group.count}
                        </Box>
                      </Box>
                      {expandedGroups[group.key] ? (
                        <Box sx={{ mt: 1.5, pl: 3, display: "flex", flexDirection: "column", gap: 1 }}>
                          {loadingGroups[group.key] && !(groupChildren[group.key] || []).length ? (
                            <Typography variant="body2" color="text.secondary">Loading...</Typography>
                          ) : (
                            (groupChildren[group.key] || [])
                              .filter((row) =>
                                groupBy === "service"
                                  ? normKey(row.service) === normKey(group.key)
                                  : normKey(row.region) === normKey(group.key),
                              )
                              .map((row) => (
                              <Box key={row.id} onClick={(event) => { event.stopPropagation(); openDevice(row); }} sx={{ cursor: "pointer" }}>
                                <Typography variant="body2" fontWeight={600} noWrap>{row.resource_name}</Typography>
                                <Typography variant="caption" color="text.secondary">{row.resource_type || "-"}</Typography>
                              </Box>
                            ))
                          )}
                        </Box>
                      ) : null}
                    </Box>
                  ))
                  : tableRows.map((row) => (
                    <Box
                      key={row.id}
                      onClick={() => openDevice(row)}
                      sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider", cursor: "pointer", display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Typography variant="subtitle2" fontWeight={700} noWrap>{row.resource_name || "-"}</Typography>
                      <Typography variant="body2" color="text.secondary">{row.account_id || "-"}</Typography>
                      <Typography variant="body2" color="text.secondary">{row.resource_type || "-"}</Typography>
                    </Box>
                  ))}
              </Box>
            </Box>
          ) : (
            <AgGridTable
              columnDefs={columnDefs}
              data={tableRows}
              showSearch
              searchPlaceholder="Search..."
              isLoading={loading}
              pagination={false}
              showAppsIcon={false}
              height="calc(100vh - 300px)"
              noRowsMessage={showingGroups ? "No groups found." : "No AWS discovery resources yet."}
              disableLocalSearch
              headerActions={headerActions}
              gridOptions={gridOptions}
              styles={{
                flex: 1,
                minHeight: 0,
                width: "100%",
                "& .ag-theme-material .ag-row.ag-row-group": {
                  backgroundColor: isDarkMode ? "rgba(255,255,255,0.02) !important" : "#F9FAFB !important",
                },
              }}
              onSearchSubmit={(value) => {
                setSearch(value || "");
                setPage(1);
                setExpandedGroups({});
                setGroupChildren({});
              }}
              onSearchClear={() => {
                setSearch("");
                setPage(1);
                setExpandedGroups({});
                setGroupChildren({});
              }}
            />
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            px: 2,
            py: 1,
            borderTop: "1px solid",
            borderColor: "divider",
            backgroundColor: isDarkMode ? "#393E4B" : "#FFFFFF",
            color: isDarkMode ? "#ffffff" : "text.primary",
            minHeight: 48,
            flex: "0 0 auto",
            position: "relative",
            zIndex: 2,
          }}
        >
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 1 }}>
            <Typography variant="body2" sx={{ color: isDarkMode ? "#ffffff" : "text.secondary", fontWeight: 500 }}>
              Page Size:
            </Typography>
            <Select
              size="small"
              value={pageSize}
              onChange={(event) => changePageSize(Number(event.target.value))}
              disabled={loading}
              sx={{
                minWidth: 72,
                height: 28,
                color: isDarkMode ? "#ffffff" : "text.primary",
                ".MuiOutlinedInput-notchedOutline": { borderColor: isDarkMode ? "rgba(255,255,255,0.24)" : "divider" },
                ".MuiSvgIcon-root": { color: isDarkMode ? "#ffffff" : "text.secondary" },
                fontSize: "0.875rem",
                fontWeight: 600,
              }}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <MenuItem key={size} value={size}>{size}</MenuItem>
              ))}
            </Select>
          </Box>
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography variant="body2" sx={{ color: isDarkMode ? "#ffffff" : "text.secondary", fontWeight: 500 }}>
              Showing {startRow} to {endRow} of {total} {showingGroups ? (groupBy === "service" ? "services" : "regions") : "resources"}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
            <IconButton size="small" onClick={() => goToPage(1)} disabled={!hasPreviousPage || loading} sx={{ p: 0.5, color: isDarkMode ? "#ffffff" : "text.primary", "&:disabled": { opacity: 0.3 } }}>
              <FirstPageIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => goToPage(page - 1)} disabled={!hasPreviousPage || loading} sx={{ p: 0.5, color: isDarkMode ? "#ffffff" : "text.primary", "&:disabled": { opacity: 0.3 } }}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            {pageNumbers.map((number) => (
              <IconButton
                key={number}
                size="small"
                onClick={() => goToPage(number)}
                disabled={loading}
                sx={{
                  p: 0.5,
                  minWidth: 28,
                  borderRadius: 1,
                  bgcolor: number === page ? "primary.main" : "transparent",
                  color: number === page ? "#ffffff" : isDarkMode ? "#ffffff" : "text.primary",
                  "&:hover": { bgcolor: number === page ? "primary.main" : "action.hover" },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, color: "inherit" }}>{number}</Typography>
              </IconButton>
            ))}
            <IconButton size="small" onClick={() => goToPage(page + 1)} disabled={!hasNextPage || loading} sx={{ p: 0.5, color: isDarkMode ? "#ffffff" : "text.primary", "&:disabled": { opacity: 0.3 } }}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => goToPage(totalPages || 1)} disabled={!hasNextPage || loading} sx={{ p: 0.5, color: isDarkMode ? "#ffffff" : "text.primary", "&:disabled": { opacity: 0.3 } }}>
              <LastPageIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Card>

      <Drawer
        anchor="right"
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: "50%" },
            overflow: "hidden",
            bgcolor: isDarkMode ? "#1E1E1E" : "#FFFFFF",
          },
        }}
      >
        <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.5, height: "100%", overflow: "auto" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" fontWeight={700}>{selected?.resource_name || "-"}</Typography>
            <IconButton aria-label="Close" onClick={() => setSelected(null)} size="small"><CloseIcon /></IconButton>
          </Box>
          <Divider />
          <Detail label="Vendor" value={selected?.vendor} />
          <Detail label="Account ID" value={selected?.account_id} />
          <Detail label="Device type" value={selected?.resource_type} />
          <Detail label="Region" value={selected?.region} />
          <Detail label="Service" value={selected?.service} />
          <Detail label="Scan type" value={selected?.scan_type} />
          <Detail label="Timestamp" value={formatWhen(selected?.last_seen || "")} />
          <Detail label="Comments" value={selected?.comments || "-"} />
          <Detail label="ARN" value={selected?.arn} />
        </Box>
      </Drawer>
    </Box>
  );
};

export default AwsInventory;
